import Sticky from 'react-stickynode';
import styled, { css } from 'styled-components';

import {
  ProductBrowserCustomFilters as ProductBrowser,
  ProductBrowserSideBar as BaseProductBrowserSideBar,
  desktop,
  TopLevelCategoryHeading,
  mobile,
} from '@techstyle/react-components';

import ProductFilters from '../ProductFilters';

const variantSidebarWidth = 244;
const newSidebarWidth = 280;
export const HEADER_OFFSET = 200;
const VARIANT_HEADER_OFFSET = 166;

export const ProductGridContent = styled.div`
  flex: 1 1 auto;
  ${desktop`
    flex: 1;
    max-width: ${props =>
      props.fullWidth ? '100%' : `calc(100% - ${variantSidebarWidth + 24}px)`};
    padding-left: 16px;
  `}
  ${mobile`
    max-width: 100%;
  `}
  ${mobile`
    max-width: 100%;
  `}
`;

export const ProductGridSideBar = styled(BaseProductBrowserSideBar)`
  ${({ isCarousel }) => isCarousel && 'margin-right: 0px;'};
  width: ${props =>
    props.newSidebarWidth ? newSidebarWidth : variantSidebarWidth + 24}px;
  margin-right: 0;
  padding-right: 24px;
  padding-left: 0;
`;

export const SidebarStickyWrapper = styled.div`
  ${({ topOffset }) =>
    `
      top: ${topOffset || VARIANT_HEADER_OFFSET - 28}px;
      max-height: calc(100vh - ${topOffset || VARIANT_HEADER_OFFSET - 28}px);
      @media (max-width: ${props => props.theme.breakpoints.xlarge}px) {
        top: calc(${topOffset || VARIANT_HEADER_OFFSET}px - 46px);
      }
    `}
  overflow-y: auto;
  overflow-x: clip;
  align-self: flex-start;
  position: sticky;
  height: fit-content;
  padding-bottom: 32px;
  ${desktop`
    min-width: ${variantSidebarWidth}px;
  `}
`;

const buildPaddings = ({ component = 'grid' }) =>
  css`
    @media (min-width: ${({ theme }) => theme.breakpoints.large}px) {
      padding: 0 16px ${component === 'grid' ? 56 : 0}px 16px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.xlarge}px) {
      padding: 0 56px ${component === 'grid' ? 72 : 0}px 56px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.xxlarge}px) {
      padding: 0 104px ${component === 'grid' ? 72 : 0}px 104px;
    }
  `;

export const ProductGridTopCategoryHeading = styled(TopLevelCategoryHeading)`
  font-size: 18px;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: ${0.3 / 18}em;
  color: ${props => props.theme.colors.default};

  ${desktop`
    display: none;
  `}
`;

export const ProductGridHeader = styled(ProductBrowser.Header)`
  height: 51px;
  padding: 0px;
  ${desktop`
        padding: 42px ${props =>
          props.isVariant
            ? props.theme.spacing.medium
            : props.theme.spacing.small}px;
        position: sticky;
        top: ${({ headerHeight }) => (headerHeight ? headerHeight - 4 : 116)}px;
        background-color: ${props => props.theme.colors.white};
        z-index: ${props => props.theme.zIndex.floatingBar};
        @media (max-width: ${props => props.theme.breakpoints.xlarge}px) {
          top: ${({ headerHeight }) =>
            headerHeight ? headerHeight - 4 : 116 - 36}px;
        };

        ${({ $filterBarEnabled }) =>
          $filterBarEnabled &&
          css`
            justify-content: flex-end;
          `}
      `}
  ${props => {
    if (props.isBuilder) {
      return 'padding: 0;';
    }
    return buildPaddings({ component: 'header' });
  }}
`;

export const ProductGridBody = styled(ProductBrowser.Body)`
  padding: 0;

  ${({ isCarousel, useTabStyles }) =>
    isCarousel &&
    `
      margin-right: ${useTabStyles ? '0' : '20'}px;
    `}

  ${({ arrowsOffset, builderBlocksHeight }) =>
    (!!arrowsOffset || !!builderBlocksHeight) &&
    `.slick-arrow {
          top: -${(arrowsOffset || 0) + (builderBlocksHeight || 0)}px;
      }
    `}

  ${({ arrowColor }) =>
    arrowColor &&
    ` .slick-arrow:not(.slick-disabled) {
          color: ${arrowColor};
        }
    `}

  ${desktop`
      ${props => {
        if (props.isBuilder && props.isCarousel) {
          return `padding: 0;`;
        }
        if (props.showGridOnly) {
          return `padding: 0 ${props.theme.spacing.medium}px ${props.theme.spacing.large}px ${props.theme.spacing.medium}px;`;
        }
        return buildPaddings({ component: 'grid' });
      }}
    `}

  ${desktop`
    ${({ $enableTopMargin, theme }) =>
      $enableTopMargin &&
      css`
        margin-top: ${theme.spacing.large}px;
      `}
  `}
`;

export const ProductGridCategoryTreeWrapper = styled.div`
  ${desktop`
        ${({ hasFilterBar }) => (!hasFilterBar ? 'padding-top: 18px;' : null)}
      `}
`;

export const ProductGridDesktopFilterTags = styled(ProductBrowser.FilterTags)`
  padding-bottom: 18px;
`;

export const ProductGridProductFilters = styled(ProductFilters)`
  > button[aria-expanded] {
    padding-left: ${props => props.theme.spacing.small}px;
  }
`;

const productGridListStyleControl = css`
  ${desktop`
      padding-top: 4px;
      padding-bottom: 0;
    `}
`;
const productGridListStyleVariant = css`
  ${desktop`
    padding-top: 0;
    padding-bottom: 24px;
    padding-right: 0;
    padding-left: 0;
  `}
`;

export const productGridSizeGridListStyle = css`
  ${desktop`
    padding-top: 0;
    padding-bottom: 24px;
    padding-right: 0;
    padding-left: 0;
    gap: 10px;
  `}
`;

export const getProductGridListStyle = (featureFlag = false) =>
  featureFlag ? productGridListStyleVariant : productGridListStyleControl;

const productGridNestedListStyleControl = css`
  ${desktop`
     padding-top: 4px;
     padding-bottom: 0;
   `}
`;

const productGridNestedListStyleVariant = css`
  ${desktop`
     padding-top: 0;
     padding-bottom: 8px;
     padding-right: 0;
     padding-left: 0;
   `}
`;

export const getProductGridNestedListStyle = (featureFlag = false) =>
  featureFlag
    ? productGridNestedListStyleVariant
    : productGridNestedListStyleControl;

export const productGridFilterFieldLabelContainerStyle = css`
  padding-right: ${props => props.theme.spacing.tiny}px;
  width: 100%;
`;

export const productGridFilterFieldLabelStyle = css`
  color: ${props => props.theme.colors.default};
  font-size: 16px;
  letter-spacing: ${0.3 / 16}em;
`;

export const productGridNewFilterFieldLabelStyle = css`
  color: ${({ theme }) => theme.colors.default};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 1.75px;
  line-height: 14px;
  text-transform: uppercase;
`;

export const productGridNestedHeadingStyle = css`
  color: ${({ theme }) => theme.colors.subdued};
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 1px;
  line-height: 12px;
  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.spacing.tiny}px;
  text-transform: uppercase;
`;

export const productGridBandSizeCheckboxProps = css`
  ${desktop`
    width: 48px;
    height: 48px;
  `}
`;

export const ProductGridSticky = styled(Sticky)`
  & > .sticky-inner-wrapper {
    z-index: 2;

    z-index: ${({ theme }) => theme.zIndex.productFilterBar};
  }

  padding-left: ${({ theme }) => theme.spacing.medium}px;
`;

export const ProductGridSortWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles}
`;

export const ProductGridWrapper = styled.div`
  ${mobile`
    padding: 0 ${props => props.theme.spacing.small}px;
  `}
`;
